import { useCallback, useEffect, useState } from 'react';
import ImageUploader from '../../components/admin/ImageUploader';
import {
  PAGE_SLUGS,
  deleteBackground,
  fetchAllBackgrounds,
  upsertBackground,
} from '../../lib/backgroundsApi';
import { deleteStorageFile } from '../../lib/storage';

export default function BackgroundsManager() {
  const [backgrounds, setBackgrounds] = useState({});
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const rows = await fetchAllBackgrounds();
    const map = {};
    rows.forEach((r) => { map[r.page_slug] = r.image_url; });
    setBackgrounds(map);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (slug, url) => {
    setSaving(slug);
    setMessage('');
    setError('');
    try {
      await upsertBackground(slug, url);
      setBackgrounds((prev) => ({ ...prev, [slug]: url }));
      setMessage(`Saved background for ${slug}`);
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const handleRemove = async (slug) => {
    if (!backgrounds[slug]) return;
    if (!window.confirm(`Remove custom background for "${slug}" and use the default image?`)) return;

    setSaving(slug);
    setMessage('');
    setError('');
    try {
      const imageUrl = await deleteBackground(slug);
      if (imageUrl) {
        try {
          await deleteStorageFile('page-backgrounds', imageUrl);
        } catch {
          // DB row is gone; storage cleanup is best-effort
        }
      }
      setBackgrounds((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      setMessage(`Removed background for ${slug}. Default image will show on the site.`);
    } catch (err) {
      setError(err.message || 'Remove failed');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-dark mb-2">Page Backgrounds</h1>
      <p className="text-gray-500 mb-8">
        Upload a new image to replace the hero background, or remove it to restore the default.
      </p>

      {message && (
        <p className="mb-4 text-sm p-3 rounded-lg bg-green-50 text-green-800">{message}</p>
      )}
      {error && (
        <p className="mb-4 text-sm p-3 rounded-lg bg-red-50 text-red-800">{error}</p>
      )}

      <div className="space-y-8">
        {PAGE_SLUGS.map(({ slug, label }) => (
          <div key={slug} className="bg-white p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="font-semibold text-brand-dark capitalize">
                {label} <span className="text-gray-400 font-normal">({slug})</span>
              </h2>
              {backgrounds[slug] && (
                <button
                  type="button"
                  disabled={saving === slug}
                  onClick={() => handleRemove(slug)}
                  className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  {saving === slug ? 'Removing…' : 'Remove background'}
                </button>
              )}
            </div>
            <ImageUploader
              bucket="page-backgrounds"
              pathPrefix={slug}
              currentUrl={backgrounds[slug]}
              onUpload={(url) => handleUpload(slug, url)}
              label={saving === slug ? 'Saving…' : 'Background image'}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
